import { Injectable } from '@angular/core'
import { UserInfo } from '@shared/models/auth/auth.models'

/**
 * JWT Service
 *
 * Handles JWT token operations including parsing, validation, and extraction of user information.
 * Provides utilities for working with JWT tokens in a type-safe manner.
 *
 * Features:
 * - JWT token parsing and validation
 * - User information extraction from tokens
 * - Token expiration checking
 * - Base64 URL decoding utilities
 */
@Injectable({ providedIn: 'root' })
export class JwtService {
  private readonly TOKEN_PREFIX = 'Bearer '

  /**
   * Parses a JWT token and extracts the payload
   * @param token JWT token string
   * @returns Parsed token payload or null if invalid
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parseToken(token: string): any | null {
    try {
      if (!token) {
        return null
      }

      // Remove Bearer prefix if present
      const cleanToken = token.startsWith(this.TOKEN_PREFIX) ? token.substring(this.TOKEN_PREFIX.length) : token

      // Split token into parts
      const parts = cleanToken.split('.')
      if (parts.length !== 3) {
        console.warn('Invalid JWT token format')
        return null
      }

      // Decode the payload (second part)
      const payload = parts[1]
      const decodedPayload = this.base64UrlDecode(payload)

      return JSON.parse(decodedPayload)
    } catch (error) {
      console.error('Error parsing JWT token:', error)
      return null
    }
  }

  /**
   * Extracts user information from a JWT token
   * @param token JWT token string
   * @returns User information or null if token is invalid
   */
  getUserInfo(token: string): UserInfo | null {
    const payload = this.parseToken(token)
    if (!payload) {
      return null
    }

    try {
      return {
        userId: payload.sub,
        role:
          payload.role_clesame ||
          payload.role ||
          payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
        exp: payload.exp,
        iat: payload.iat,
        iss: payload.iss,
        aud: payload.aud,
        jti: payload.jti,
      }
    } catch (error) {
      console.error('Error extracting user info from token:', error)
      return null
    }
  }

  /**
   * Checks if a JWT token is expired
   * @param token JWT token string
   * @returns Whether the token is expired
   */
  isTokenExpired(token: string): boolean {
    const payload = this.parseToken(token)
    if (!payload || !payload.exp) {
      return true
    }

    // Convert expiration time to milliseconds and compare with current time
    const expirationTime = payload.exp * 1000
    const currentTime = Date.now()

    return currentTime >= expirationTime
  }

  /**
   * Gets the expiration time of a JWT token
   * @param token JWT token string
   * @returns Expiration time as Date object or null if invalid
   */
  getTokenExpiration(token: string): Date | null {
    const payload = this.parseToken(token)
    if (!payload || !payload.exp) {
      return null
    }

    return new Date(payload.exp * 1000)
  }

  /**
   * Gets the time until token expiration
   * @param token JWT token string
   * @returns Time until expiration in milliseconds, or 0 if expired/invalid
   */
  getTimeUntilExpiration(token: string): number {
    const expiration = this.getTokenExpiration(token)
    if (!expiration) {
      return 0
    }

    const timeUntilExpiration = expiration.getTime() - Date.now()
    return Math.max(0, timeUntilExpiration)
  }

  /**
   * Checks if a token will expire within a specified time window
   * @param token JWT token string
   * @param windowMs Time window in milliseconds
   * @returns Whether the token will expire within the window
   */
  willExpireWithin(token: string, windowMs: number): boolean {
    const timeUntilExpiration = this.getTimeUntilExpiration(token)
    return timeUntilExpiration > 0 && timeUntilExpiration <= windowMs
  }

  /**
   * Validates a JWT token format and basic structure
   * @param token JWT token string
   * @returns Whether the token has a valid format
   */
  isValidFormat(token: string): boolean {
    if (!token || typeof token !== 'string') {
      return false
    }

    const cleanToken = token.startsWith(this.TOKEN_PREFIX) ? token.substring(this.TOKEN_PREFIX.length) : token

    const parts = cleanToken.split('.')
    return parts.length === 3 && parts.every(part => part.length > 0)
  }

  /**
   * Decodes a base64 URL encoded string
   * @param str Base64 URL encoded string
   * @returns Decoded string
   */
  private base64UrlDecode(str: string): string {
    // Add padding if needed
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/')
    while (base64.length % 4) {
      base64 += '='
    }

    try {
      return atob(base64)
    } catch (error) {
      console.error('Error decoding base64 URL string:', error)
      throw new Error('Invalid base64 URL string')
    }
  }

  /**
   * Creates a token with Bearer prefix
   * @param token Raw token string
   * @returns Token with Bearer prefix
   */
  addBearerPrefix(token: string): string {
    return token.startsWith(this.TOKEN_PREFIX) ? token : `${this.TOKEN_PREFIX}${token}`
  }

  /**
   * Removes Bearer prefix from token
   * @param token Token string
   * @returns Token without Bearer prefix
   */
  removeBearerPrefix(token: string): string {
    return token.startsWith(this.TOKEN_PREFIX) ? token.substring(this.TOKEN_PREFIX.length) : token
  }
}
